import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Icon, formatNumber } from '@column/column-ui-kit';
import { DataTable, type DataTableColumnVisibility, type DataTableProps, Button as UIButton } from '@column/ui';

import { defaultACHColumns, useACHTransfersEditTable } from '../_utils/ach';
import { defaultBookColumns, useBookTransfersEditTable } from '../_utils/book';
import { withAmountFooter } from '../_utils/shared';
import { defaultWireColumns, useWireTransfersEditTable } from '../_utils/wire';
import { TransferTemplateForm } from '../types';
import { useDialog } from '~/components/Dialog/Provider';
import {
  AchTransferTemplateResponse,
  BookTransferTemplateResponse,
  TransferTemplateType,
  WireTransferTemplateResponse,
} from '~/repositories/Transfer/TransferTemplateRepository';

interface EditTemplateTableProps {
  type: TransferTemplateType;
}

const TableWrapper = styled.div`
  display: grid;
  gap: 8px;
`;

const AddButton = styled(Button)`
  --button-scale: 1;

  &:after {
    background: ${({ theme }) => theme.gray.blendToBackground(25)};
  }

  &:hover {
    &:after {
      background: ${({ theme }) => theme.gray.blendToBackground(50)};
    }
  }
`;

const TotalAmount = styled.span`
  color: ${({ theme }) => theme.secondary.blendToForeground(400)};
  font-weight: 500;
`;

export const EditTemplateTable: FC<EditTemplateTableProps> = ({ type }) => {
  const { openAlert } = useDialog();
  const { setValue } = useFormContext<TransferTemplateForm>();

  useEffect(() => {
    switch (type) {
      case TransferTemplateType.ACH:
        setValue('visibleColumns', defaultACHColumns);
        break;
      case TransferTemplateType.Wire:
        setValue('visibleColumns', defaultWireColumns);
        break;
      case TransferTemplateType.Book:
        setValue('visibleColumns', defaultBookColumns);
        break;
    }
  }, [type, setValue]);

  const {
    append: achAppend,
    remove: achRemove,
    data: achTransfers,
    columns: achColumns,
    columnVisibility: achColumnVisibility,
  } = useACHTransfersEditTable();

  const {
    append: wireAppend,
    remove: wireRemove,
    data: wireTransfers,
    columns: wireColumns,
    columnVisibility: wireColumnVisibility,
  } = useWireTransfersEditTable();

  const {
    append: bookAppend,
    data: bookTransfers,
    columns: bookColumns,
    columnVisibility: bookColumnVisibility,
    remove: bookRemove,
  } = useBookTransfersEditTable();

  const { total, count } = useMemo(() => {
    const rows =
      type === TransferTemplateType.ACH
        ? achTransfers
        : type === TransferTemplateType.Wire
          ? wireTransfers
          : bookTransfers;
    return {
      total: (rows ?? []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
      count: rows?.length ?? 0,
    };
  }, [type, achTransfers, wireTransfers, bookTransfers]);

  const handleAddClick = useCallback(() => {
    if (type === TransferTemplateType.ACH) {
      achAppend();
    }
    if (type === TransferTemplateType.Wire) {
      wireAppend();
    }
    if (type === TransferTemplateType.Book) {
      bookAppend();
    }
  }, [type, achAppend, wireAppend, bookAppend]);

  const handleDeleteClick = useCallback(
    (id?: string) => {
      if (!id) {
        return;
      }

      openAlert({
        headline: 'Remove transfer?',
        content: 'Are you sure you want to remove this transfer and the related data?',
        confirmButton: {
          label: 'Remove',
          variant: 'danger',
        },
        onSuccess: () => {
          if (type === TransferTemplateType.ACH) {
            achRemove(id);
          } else if (type === TransferTemplateType.Wire) {
            wireRemove(id);
          } else if (type === TransferTemplateType.Book) {
            bookRemove(id);
          }
        },
      });
    },
    [type, achRemove, wireRemove, bookRemove, openAlert]
  );

  const handleColumnVisibilityChange = useCallback(
    (visibilityState: DataTableColumnVisibility) => {
      setValue(
        'visibleColumns',
        Object.entries(visibilityState)
          .filter(([, visible]) => visible)
          .map(([id]) => id)
      );
    },
    [setValue]
  );

  const columnVisibility =
    type === TransferTemplateType.ACH
      ? achColumnVisibility
      : type === TransferTemplateType.Wire
        ? wireColumnVisibility
        : bookColumnVisibility;
  const showTotal = count > 1 && (columnVisibility as Record<string, boolean>).amount !== false;
  const totalFooter = showTotal ? <TotalAmount>{formatNumber(total, 'USD')}</TotalAmount> : undefined;

  const tableProps: Pick<
    DataTableProps<AchTransferTemplateResponse | WireTransferTemplateResponse | BookTransferTemplateResponse>,
    'emptyMessage' | 'footer' | 'rowKey' | 'rowAction'
  > = {
    emptyMessage: 'No entries',
    footer: showTotal ? 'Total' : undefined,
    rowKey: (row) => row.transferTemplateRowId ?? '',
    rowAction: (row) => (
      <UIButton
        type="button"
        size="icon-xs"
        variant="muted"
        aria-label="Remove transfer"
        className="[&_svg]:[--icon-size-x:12px] [&_svg]:[--icon-size-y:12px] [&_svg]:[--icon-size:12px]"
        onClick={() => handleDeleteClick(row.transferTemplateRowId)}
      >
        <Icon.Trash />
      </UIButton>
    ),
  };

  return (
    <TableWrapper>
      {type === TransferTemplateType.ACH && (
        <DataTable<AchTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={withAmountFooter(achColumns, totalFooter)}
          columnVisibility={achColumnVisibility}
          data={achTransfers}
          headerAction={
            <DataTable.ColumnSelector<AchTransferTemplateResponse & { isAuthorized?: boolean }>
              columns={achColumns}
              onValueChange={handleColumnVisibilityChange}
              value={achColumnVisibility}
              title="Add Parameters"
            />
          }
          variant="grid"
          {...tableProps}
        />
      )}

      {type === TransferTemplateType.Wire && (
        <DataTable<WireTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={withAmountFooter(wireColumns, totalFooter)}
          columnVisibility={wireColumnVisibility}
          data={wireTransfers}
          headerAction={
            <DataTable.ColumnSelector<WireTransferTemplateResponse & { isAuthorized?: boolean }>
              columns={wireColumns}
              onValueChange={handleColumnVisibilityChange}
              value={wireColumnVisibility}
              title="Add Parameters"
            />
          }
          variant="grid"
          {...tableProps}
        />
      )}

      {type === TransferTemplateType.Book && (
        <DataTable<BookTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={withAmountFooter(bookColumns, totalFooter)}
          columnVisibility={bookColumnVisibility}
          data={bookTransfers}
          headerAction={
            <DataTable.ColumnSelector<BookTransferTemplateResponse & { isAuthorized?: boolean }>
              columns={bookColumns}
              onValueChange={handleColumnVisibilityChange}
              value={bookColumnVisibility}
              title="Add Parameters"
            />
          }
          variant="grid"
          {...tableProps}
        />
      )}

      <AddButton type="button" size="small" fullWidth variant="muted" onClick={handleAddClick} ariaLabel="Add row">
        <Icon.Plus />
      </AddButton>
    </TableWrapper>
  );
};
